import { useState, type ImgHTMLAttributes } from 'react'

import { cn } from '~/lib/cn'

type ImageState = 'loading' | 'ready' | 'error'

/**
 * The quiet pulse a picture leaves in its place while it loads. The pulse rides a child of
 * an opaque backing rather than fading the surface itself: whatever the skeleton stands in
 * front of — an image that arrived before its siblings, say — must never ghost through it.
 */
export function ImageSkeleton({ label }: { label?: string }) {
  return (
    <span
      role="status"
      {...(label ? { 'aria-label': label } : {})}
      className="absolute inset-0 bg-surface-raised"
    >
      <span className="absolute inset-0 animate-pulse bg-gradient-to-br from-surface-raised to-surface-bright" />
    </span>
  )
}

interface SafeImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  readonly src: string
  /** Sizes and positions the frame; image classes only style the image inside it. */
  readonly frameClassName?: string
}

/** A remote image whose loading and failure remain part of the app instead of browser chrome. */
export function SafeImage({
  src,
  alt,
  className = '',
  frameClassName = '',
  onLoad,
  onError,
  style,
  ...props
}: SafeImageProps) {
  const [settled, setSettled] = useState<{ src: string; state: ImageState }>({
    src,
    state: 'loading',
  })
  const state = settled.src === src ? settled.state : 'loading'

  return (
    // Merged, not concatenated: a frame told to be `absolute` must not also stay `relative`.
    // With both classes on the element the stylesheet's order picks the winner, and it picked
    // `relative` — which put the frame into the flow of the set viewer's media box and pushed
    // the video below the screen, where the browser pauses an offscreen muted autoplay for good.
    <span className={cn('relative block overflow-hidden bg-surface-raised', frameClassName)}>
      {state === 'loading' && <ImageSkeleton />}
      {state === 'error' && (
        <span
          {...(alt ? { role: 'img', 'aria-label': 'Image unavailable' } : { 'aria-hidden': true })}
          className="absolute inset-0 grid place-items-center bg-surface-raised text-ink-muted"
        >
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            className="size-1/3 min-h-6 min-w-6"
            fill="currentColor"
          >
            <path d="M21 19V5a2 2 0 0 0-2-2H5c-.5 0-.96.19-1.31.5L20.5 20.31c.31-.35.5-.81.5-1.31ZM3.27 5.39A2 2 0 0 0 3 6v13a2 2 0 0 0 2 2h13.61L3.27 5.39ZM8.5 11.5l2.5 3.01L12.5 16l1.23-1.48L18.2 19H5l3.5-7.5Z" />
          </svg>
        </span>
      )}
      <img
        {...props}
        src={src}
        alt={alt}
        style={{ ...style, opacity: state === 'ready' ? style?.opacity : 0 }}
        onLoad={(event) => {
          setSettled({ src, state: 'ready' })
          onLoad?.(event)
        }}
        onError={(event) => {
          setSettled({ src, state: 'error' })
          onError?.(event)
        }}
        className={className}
      />
    </span>
  )
}
